/**
 * Timetable card colours.
 *
 * Kept out of the styles file because this is an algorithm, not a stylesheet: it decides which
 * teacher gets which colour by looking at who ends up next to whom on the actual grid.
 */

/**
 * Card colours, generated rather than hand-listed.
 *
 * Colours are assigned per teacher (see `colorOf` in Timetable.js) and there are ~54 active
 * teachers, so no hand-picked list is big enough. See `paletteColour` below for how the set
 * is sized to what is actually on screen.
 *
 * Generated in LCh rather than HSL, because HSL's `lightness` is not perceptual: at l=46% a
 * yellow is far brighter than a blue, so a fixed-lightness HSL ramp leaves the yellows without
 * enough contrast to carry white label text. Fixing L in LCh instead gives every colour the
 * same perceived brightness, which pins white-text contrast across the whole palette (measured
 * 3.9:1 – 9.4:1, nothing below 3.5:1).
 *
 * Layout: 16 hues walked with a stride of 7 (co-prime with 16, so consecutive indices land on
 * opposite sides of the wheel — measured neighbour ΔE ≥ 62), across three lightness bands,
 * each band offset a third of a hue step so bands never collide. Closest pair overall is
 * ΔE 8.3, two dark cyans that only meet if the same screen shows teacher #24 and #31.
 *
 * Must stay 6-digit hex — some styles append an alpha suffix (`${props.$color}22`).
 */
const labInv = (t) => (t ** 3 > 0.008856 ? t ** 3 : (t - 16 / 116) / 7.787);
const gammaEncode = (v) => (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055);

const lchToLinearRgb = (L, C, hueDeg) => {
  const a = C * Math.cos((hueDeg * Math.PI) / 180);
  const b = C * Math.sin((hueDeg * Math.PI) / 180);
  const fy = (L + 16) / 116;
  const X = 0.95047 * labInv(fy + a / 500);
  const Y = labInv(fy);
  const Z = 1.08883 * labInv(fy - b / 200);
  return [
    3.2406 * X - 1.5372 * Y - 0.4986 * Z,
    -0.9689 * X + 1.8758 * Y + 0.0415 * Z,
    0.0557 * X - 0.204 * Y + 1.057 * Z
  ];
};

const inGamut = (rgb) => rgb.every(v => v >= -0.001 && v <= 1.001);

/** Reduce chroma until the colour fits sRGB — saturated cyans and greens sit outside it. */
const lchToHex = (L, chroma, hueDeg) => {
  let C = chroma;
  if (!inGamut(lchToLinearRgb(L, C, hueDeg))) {
    let lo = 0;
    let hi = C;
    for (let i = 0; i < 24; i += 1) {
      const mid = (lo + hi) / 2;
      if (inGamut(lchToLinearRgb(L, mid, hueDeg))) lo = mid;
      else hi = mid;
    }
    C = lo;
  }
  return `#${lchToLinearRgb(L, C, hueDeg)
    .map(v => Math.round(255 * gammaEncode(Math.min(1, Math.max(0, v))))
      .toString(16)
      .padStart(2, '0'))
    .join('')}`;
};

const BANDS = [
  { L: 42, C: 52 },
  { L: 30, C: 46 },
  { L: 54, C: 44 }
];

const hexToLab = (hex) => {
  const toLinear = (v) => (v > 0.04045 ? ((v + 0.055) / 1.055) ** 2.4 : v / 12.92);
  const [r, g, b] = [1, 3, 5].map(i => toLinear(parseInt(hex.slice(i, i + 2), 16) / 255));
  const x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
  const y = r * 0.2126 + g * 0.7152 + b * 0.0722;
  const z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;
  const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  return [116 * f(y) - 16, 500 * (f(x) - f(y)), 200 * (f(y) - f(z))];
};

const deltaE = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

/** Candidate colours: the hue circle sampled finely, in each lightness band. */
const candidatesFor = (bandCount) => {
  const out = [];
  for (let band = 0; band < bandCount; band += 1) {
    const { L, C } = BANDS[band];
    for (let step = 0; step < 72; step += 1) {
      const hex = lchToHex(L, C, step * 5);
      out.push({ hex, lab: hexToLab(hex) });
    }
  }
  return out;
};

/**
 * Pick `count` colours that are as far apart as possible, by farthest-point sampling.
 *
 * Evenly spaced HUES are not evenly spaced COLOURS. sRGB's gamut is nowhere near circular —
 * it is pinched hard in the cyans and greens — so `lchToHex` has to cut chroma to stay
 * inside it, and a whole stretch of the hue circle collapses into near-identical colours.
 * Spacing by hue put two of Claremont's teachers 45 degrees apart and still produced two
 * dark teals nobody could tell apart.
 *
 * So spacing is measured in the space that decides the answer: sample the circle finely, then
 * repeatedly take the candidate whose nearest already-chosen colour is furthest away. That
 * naturally spends few entries on the pinched cyans and more on the roomy reds and blues.
 *
 * Deterministic — fixed candidate list, fixed start — so the same team size always yields the
 * same palette in the same order.
 */
const buildPalette = (count) => {
  // One lightness band reads as a set; only reach for the others when the circle runs out.
  const candidates = candidatesFor(count <= 16 ? 1 : BANDS.length);
  const chosen = [candidates[0]];
  while (chosen.length < count && chosen.length < candidates.length) {
    let best = null;
    let bestDistance = -1;
    candidates.forEach(candidate => {
      const nearest = chosen.reduce(
        (min, taken) => Math.min(min, deltaE(candidate.lab, taken.lab)),
        Infinity
      );
      if (nearest > bestDistance) {
        bestDistance = nearest;
        best = candidate;
      }
    });
    chosen.push(best);
  }
  return chosen.map(c => c.hex);
};

// Palettes are pure functions of their size and are asked for on every render, so memoise.
const paletteCache = new Map();

const paletteOf = (count) => {
  const size = Math.max(1, Math.min(count, 48));
  if (!paletteCache.has(size)) paletteCache.set(size, buildPalette(size));
  return paletteCache.get(size);
};

/**
 * The colour for entry `index` out of `total`.
 *
 * Sized to the set being coloured rather than taken from a fixed list, because what matters
 * is that the colours ON SCREEN TOGETHER are far apart. A fixed palette can only promise that
 * *consecutive* indices differ, and a day's grid shows a sparse subset of the week's teachers
 * — for those the promise buys nothing.
 *
 * Stable for as long as the set is: the caller passes the index from the backend's
 * name-sorted teacher axis for the whole week, so a colour does not move when you switch
 * view, change day tab or apply a filter.
 *
 * Must stay 6-digit hex — some styles append an alpha suffix (`${props.$color}22`).
 */
export const paletteColour = (index, total) => {
  const palette = paletteOf(total);
  return palette[index % palette.length];
};

/** Fixed-size palette, for colouring by class where the set is far larger than any palette. */
export const COLORS = paletteOf(48);

/** Cards with nobody assigned; deliberately outside the palette so it reads as "no teacher". */
export const NO_TEACHER_COLOR = '#9ca3af';


// ==================== Colouring by who sits next to whom ====================

/**
 * Two classes count as visually adjacent if they are close enough in time that the eye
 * compares them. Back-to-back is the obvious case; a short gap still reads as "next to".
 */
const ADJACENT_GAP_MINUTES = 30;

const toMinutes = (time) => {
  const [h, m] = (time || '0:0').split(':').map(Number);
  return h * 60 + m;
};

/** Every teacher on an occurrence — a class with two teachers puts both in the graph. */
const teacherIdsOf = (occurrence) =>
  (occurrence.teachers || []).map(t => t.id).filter(Boolean);

/**
 * Which pairs of teachers end up beside each other on the grid.
 *
 * Two kinds of adjacency, matching what the grid actually draws:
 *  1. same lane, near in time — one class ends and the next begins (or they overlap and get
 *     split into side-by-side halves, which is when telling them apart matters most);
 *  2. neighbouring lanes, overlapping in time — the columns literally touch.
 *
 * Built over the whole week rather than the visible day, so a colour does not change when you
 * switch day tab or filter. That costs a few edges that are never seen together, which only
 * makes the result more conservative.
 */
const buildTeacherAdjacency = (occurrences, roomKeys) => {
  const graph = new Map();
  const connect = (a, b) => {
    if (a === b) return;
    if (!graph.has(a)) graph.set(a, new Set());
    if (!graph.has(b)) graph.set(b, new Set());
    graph.get(a).add(b);
    graph.get(b).add(a);
  };
  const connectAcross = (first, second) => {
    teacherIdsOf(first).forEach(a => teacherIdsOf(second).forEach(b => connect(a, b)));
  };

  const laneOrder = new Map(roomKeys.map((key, index) => [key, index]));

  // date -> roomKey -> occurrences
  const byDate = new Map();
  occurrences.forEach(occurrence => {
    if (occurrence.isBusy || occurrence.status === 'cancelled') return;
    if (!byDate.has(occurrence.date)) byDate.set(occurrence.date, new Map());
    const lanes = byDate.get(occurrence.date);
    const key = occurrence.roomKey || '';
    if (!lanes.has(key)) lanes.set(key, []);
    lanes.get(key).push({
      startMin: toMinutes(occurrence.startTime),
      endMin: toMinutes(occurrence.endTime),
      teachers: teacherIdsOf(occurrence)
    });
  });

  byDate.forEach(lanes => {
    lanes.forEach(items => items.sort((a, b) => a.startMin - b.startMin));

    // (1) within a lane: near in time
    lanes.forEach(items => {
      for (let i = 0; i < items.length; i += 1) {
        for (let j = i + 1; j < items.length; j += 1) {
          // Sorted by start, so once one is too far away every later one is too.
          if (items[j].startMin - items[i].endMin > ADJACENT_GAP_MINUTES) break;
          connectAcross(items[i], items[j]);
        }
      }
    });

    // (2) between neighbouring lanes: overlapping in time
    const present = [...lanes.keys()]
      .filter(key => laneOrder.has(key))
      .sort((a, b) => laneOrder.get(a) - laneOrder.get(b));

    for (let i = 0; i + 1 < present.length; i += 1) {
      const left = lanes.get(present[i]);
      const right = lanes.get(present[i + 1]);
      left.forEach(a => right.forEach(b => {
        if (a.startMin < b.endMin && b.startMin < a.endMin) connectAcross(a, b);
      }));
    }
  });

  return graph;
};

/** Past this the eye no longer benefits, so extra separation should go to another pair. */
const SEPARATION_TARGET = 45;

/**
 * Assign each teacher a colour, keeping colours apart where it actually matters.
 *
 * This is graph colouring rather than an even spread of the palette. Spacing colours evenly
 * treats all pairs as equally important, but only pairs that appear beside each other need to
 * be distinguishable — and there are far fewer of those than there are pairs of teachers. So:
 * build the adjacency, then colour the busiest teachers first (Welsh-Powell ordering, since
 * the most-constrained node is the one worth spending the best colour on) and give each the
 * candidate whose nearest already-assigned neighbour is furthest away.
 *
 * Separation from neighbours dominates; separation from everyone else breaks ties, so
 * unrelated teachers still tend to differ.
 *
 * Deterministic: fixed candidate list, and ties in the ordering broken by id.
 *
 * `locked` holds colours already shown to the user. Those are never reassigned — a teacher
 * changing colour underneath someone who has been reading the grid is worse than a slightly
 * suboptimal palette — but they do constrain what the remaining teachers may take, so a newly
 * seen teacher still avoids the colours of the people they sit next to.
 */
export const assignTeacherColours = (teachers, occurrences, roomKeys, locked = new Map()) => {
  const result = new Map();
  if (!teachers.length) return result;

  const graph = buildTeacherAdjacency(occurrences, roomKeys);
  // Sized against everyone coloured so far, not just this batch: colours accumulate across
  // campus and week changes, so a page that has already handed out 20 needs the wider set
  // even if the current view only shows 6.
  const coloured = new Set([...locked.keys(), ...teachers.map(t => t.id)]).size;
  const candidates = candidatesFor(coloured <= 16 ? 1 : BANDS.length);

  const chosenLabs = [];
  teachers.forEach(teacher => {
    const held = locked.get(teacher.id);
    if (held) {
      result.set(teacher.id, held);
      chosenLabs.push(hexToLab(held));
    }
  });

  const pending = teachers.filter(teacher => !locked.has(teacher.id));

  const order = pending.sort((a, b) => {
    const degree = (graph.get(b.id)?.size || 0) - (graph.get(a.id)?.size || 0);
    return degree !== 0 ? degree : String(a.id).localeCompare(String(b.id));
  });

  order.forEach(teacher => {
    const neighbourLabs = [...(graph.get(teacher.id) || [])]
      .map(id => result.get(id))
      .filter(Boolean)
      .map(hexToLab);

    let best = candidates[0];
    let bestScore = -1;
    candidates.forEach(candidate => {
      const nearNeighbour = neighbourLabs.length
        ? Math.min(...neighbourLabs.map(lab => deltaE(candidate.lab, lab)))
        : SEPARATION_TARGET;
      const nearAnyone = chosenLabs.length
        ? Math.min(...chosenLabs.map(lab => deltaE(candidate.lab, lab)))
        : SEPARATION_TARGET;
      // Neighbours first, everyone else as the tie-break.
      const score = Math.min(nearNeighbour, SEPARATION_TARGET) * 1000
        + Math.min(nearAnyone, SEPARATION_TARGET);
      if (score > bestScore) {
        bestScore = score;
        best = candidate;
      }
    });

    result.set(teacher.id, best.hex);
    chosenLabs.push(best.lab);
  });

  return result;
};
